import { client } from '../../../services/apiClient';
import { getDefaultRange } from '../../../utils/utils';
import { format } from 'date-fns';
import { ContextInsightsResponse } from '../../../models/context-insights/ContextInsightsResponse';
import { ChatType } from '../../../types/monitor/ChatType';
import { PeriodType } from '../../../types/prompt/PeriodType';

const CONTEXT_INSIGHTS_ENDPOINT = '/context-insights';

export interface InsightPeriodRange {
  periodStart: string;
  periodEnd: string;
}

export const getContextInsightPeriods = async (projectId: number): Promise<InsightPeriodRange[]> => {
  const { data } = await client(true, true).get<InsightPeriodRange[]>(
    `${CONTEXT_INSIGHTS_ENDPOINT}/${projectId}/periods`
  );
  return data;
};

export const getContextInsights = async (
  projectId: number,
  type: ChatType,
  monitorPeriod: PeriodType,
  from: Date | null,
  to: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<ContextInsightsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${CONTEXT_INSIGHTS_ENDPOINT}/${projectId}/${type}/${monitorPeriod}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    params.set('models', filterMonitorTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<ContextInsightsResponse>(url);
  return data;
};

export type ContextTopicsAnalysisExportLang = 'en' | 'pl';

export const exportContextTopicsAnalysisCsv = async (
  projectId: number,
  type: ChatType,
  monitorPeriod: PeriodType,
  from: Date | null,
  to: Date | null,
  lang: ContextTopicsAnalysisExportLang,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  const fromDate = from ? new Date(from) : defaultRange[0];
  const toDate = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(fromDate, 'yyyy-MM-dd');
  const formattedTo = format(toDate, 'yyyy-MM-dd');

  let url = `${CONTEXT_INSIGHTS_ENDPOINT}/export-topics-analysis/${projectId}/${type}/${monitorPeriod}/${formattedFrom}/${formattedTo}/${lang}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    params.set('models', filterMonitorTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<Blob>(url, { responseType: 'blob' });
  return data;
};

export const exportContextBrandPresenceCsv = async (
  projectId: number,
  type: ChatType,
  monitorPeriod: PeriodType,
  from: Date | null,
  to: Date | null,
  lang: ContextTopicsAnalysisExportLang,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  const fromDate = from ? new Date(from) : defaultRange[0];
  const toDate = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(fromDate, 'yyyy-MM-dd');
  const formattedTo = format(toDate, 'yyyy-MM-dd');

  let url = `${CONTEXT_INSIGHTS_ENDPOINT}/export-brand-presence/${projectId}/${type}/${monitorPeriod}/${formattedFrom}/${formattedTo}/${lang}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterMonitorTypes && filterMonitorTypes.length > 0) {
    params.set('models', filterMonitorTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<Blob>(url, { responseType: 'blob' });
  return data;
};
